import { useState, useEffect } from "react";
import "./admission-test.css";
import Logo from "../../../../ui/atoms/logo/Logo";
import Button from "../../../../ui/atoms/buttons/Button";
import InputSelect from "../../../../ui/atoms/inputs/input-select/InputSelect";
import CheckBox from "../../../../ui/atoms/checkbox/CheckBox";
import RadioButton from "../../../../ui/atoms/radio-button/RadioButton";
import CustomizedTextEditor from "../../../../ui/organisms/customized-text-editor/CustomizedTextEditor";
import StatusComponent from "../../../../ui/atoms/status/StatusComponent";
import { useSelector } from "react-redux";
import {
  capitalizeAll,
  isEmpty,
  wait,
  convertDuration,
} from "../../../../core/utils/helpers";
import { onHandleSingleFile } from "../../../../core/utils/common-validators";
import ChallengeThemeQuestionItem from "../../../challenge/components/challenge-theme-question-item/ChallengeThemeQuestionItem";
import NotFound from "../../../../ui/organisms/not-found/404";

import { PiFileAudioBold } from "react-icons/pi";
import { SlClose, SlPicture } from "react-icons/sl";
import moment from "moment";
import Swal from "sweetalert2";

import {
  onCreateClassAdmissionTestQuestions,
  onGetClassAdmissionTestQuestionsWithPaginationByClassId,
  onReleaseClassAdmissionTestQuestion,
  onArchiveClassAdmissionTestQuestion,
} from "../../../student/services/classTest";
import useAxiosPrivate from "../../../../app/providers/context-provider/useAxiosPrivate";

const AdmissionTest = () => {
  const axiosPrivate = useAxiosPrivate();
  const currentStream = useSelector(
    (state) => state?.schoolState?.currentStream,
  );

  const _stream_created_at = currentStream?.classe_created_at;
  const _stream_id = currentStream?.classe_id;
  const _stream_title = currentStream?.classe_title;

  const [isMoreQuestions, setIsMoreQuestions] = useState(false);
  const [admissionTestData, setAdmissionTestData] = useState({});
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  //
  //
  const [isSending, setIsSending] = useState(false);
  //
  const [questionType, setQuestionType] = useState("");
  const [questionDifficulty, setQuestionDifficulty] = useState("");
  const [questionLanguage, setQuestionLanguage] = useState("");
  const [points, setPoints] = useState(10);
  const [timing, setTiming] = useState(10);
  const [randomAnswerOrder, setRandomAnswerOrder] = useState(false);
  const [questionDescription, setQuestionDescription] = useState("");
  const [questionExplanation, setQuestionExplanation] = useState("");
  const [questionAnswer, setQuestionAnswer] = useState("");
  const [questionAnswers, setQuestionAnswers] = useState([]);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  //
  const [questionsSet, setQuestionsSet] = useState([]);
  //
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;

    const _localData = {
      stream_id: _stream_id,
      rows_per_page: rowsPerPage,
      current_page: currentPage,
    };

    onGetClassAdmissionTestQuestionsWithPaginationByClassId(axiosPrivate,
      _localData,
      signal,
    ).then((response) => {
      const _data = response?.data;
      setAdmissionTestData(_data);
    });

    return () => {
      isMounted = false;
      isMounted && controller.abort();
    };
  }, [isMoreQuestions, isSending, rowsPerPage, currentPage]);

  const removeFile = (type) => {
    if (type === "audio") {
      setAudioFile(null);
      setAudioPreviewUrl(null);
    }

    if (type === "image") {
      setImageFile(null);
      setImagePreviewUrl(null);
    }
  };

  const onAddQuestion = () => {
    if (questionType === "") {
      Swal.fire({
        title: "Question Type Not Selected",
        text: "Please select a question type before adding a question",
        icon: "warning",
      });
      return;
    }
    if (questionDifficulty === "") {
      Swal.fire({
        title: "Question Difficulty Not Selected",
        text: "Please select the difficulty level of question",
        icon: "warning",
      });
      return;
    }
    if (questionLanguage === "") {
      Swal.fire({
        title: "Question Language Not Selected",
        text: "Please select the language of question",
        icon: "warning",
      });
      return;
    }
    if (questionDescription === "") {
      Swal.fire({
        title: "Question Description Not Provided",
        text: "Please provide a description for the question.",
        icon: "warning",
      });
      return;
    }
    if (questionExplanation === "") {
      Swal.fire({
        title: "Question Explanation Not Provided",
        text: "Please provide an explanation for the question.",
        icon: "warning",
      });
      return;
    }
    if (questionAnswers.length < 2) {
      Swal.fire({
        title: "Question Answers Not Provided",
        text: "Please provide answers for the question. At least 2 answers",
        icon: "warning",
      });
      return;
    }
    if (!isEmpty(questionAnswers)) {
      let counter = 0;
      for (const row of questionAnswers) {
        counter = row.is_correct ? counter + 1 : counter;
      }
      if (counter === 0) {
        Swal.fire({
          title: "Question Composition Answers",
          text: `You should choose ${
            questionType === "OCA" ? "one" : "at least one"
          } right answer among answers!`,
          icon: "warning",
        });
        return false;
      }
    }
    // Add new question
    setQuestionsSet((prevState) => [
      ...prevState,
      {
        type: questionType,
        difficulty: questionDifficulty,
        language: questionLanguage,
        points: points,
        timing: timing,
        random_answer_order: randomAnswerOrder,
        qv_cover_audio: audioFile,
        qv_cover_image: imageFile,
        qv_description: questionDescription,
        qv_explanations: questionExplanation,
        qv_answers: questionAnswers,
      },
    ]);
    //
    onQuestionReInitializeFields();
  };

  const onRemoveQuestion = (idx) => {
    setQuestionsSet((prev) => prev.filter((_, i) => i !== idx));
  };

  const onAddAnswer = () => {
    const _questionAnswer = questionAnswer.trim();
    if (_questionAnswer === "") {
      Swal.fire({
        title: "Empty Answer Field",
        text: "Please enter an answer before push it.",
        icon: "warning",
      });
      return;
    }
    // Check if the answer already exists (case-insensitive)
    const alreadyExists = questionAnswers.some(
      (ans) =>
        ans.answer.trim().toLowerCase() === questionAnswer.trim().toLowerCase(),
    );
    if (alreadyExists) {
      Swal.fire({
        title: "Duplicate Answer",
        text: "This answer already exists. Please enter a different one.",
        icon: "error",
      });
      return;
    }
    // Add new answer
    setQuestionAnswers((prevState) => [
      ...prevState,
      {
        is_correct: false,
        answer: questionAnswer,
      },
    ]);
    // Clear input
    setQuestionAnswer("");
  };

  const onHandleRightAnswers = (idx) => {
    setQuestionAnswers((prev) =>
      questionType === "OCA"
        ? prev.map((item, i) => ({
            ...item,
            is_correct: i === idx,
          }))
        : prev.map((item, i) =>
            i === idx ? { ...item, is_correct: !item.is_correct } : item,
          ),
    );
  };

  const onRemoveAnswer = (idx) => {
    setQuestionAnswers((prev) => prev.filter((_, i) => i !== idx));
  };

  const onQuestionReInitializeFields = () => {
    setQuestionType("");
    setQuestionDifficulty("");
    setQuestionLanguage("");
    setPoints(10);
    setTiming(10);
    setRandomAnswerOrder(false);
    setAudioFile(null);
    setAudioPreviewUrl(null);
    setImageFile(null);
    setImagePreviewUrl(null);
    setQuestionDescription("");
    setQuestionExplanation("");
    //
    setQuestionAnswers([]);
  };

  const onSubmit = async () => {
    setIsSending(true);
    Swal.fire({
      title: "Creating questions...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await wait(700);
    //
    //
    let formData = new FormData();
    //
    formData.append("stream_id", _stream_id);
    //
    // Pour questionsSet, séparer fichiers et data JSON
    const questionsData = questionsSet.map((q, _) => ({
      ...q,
      qv_cover_audio: undefined,
      qv_cover_image: undefined,
    }));
    formData.append("questions", JSON.stringify(questionsData));
    //
    // Ajouter les fichiers avec un nom unique par question
    questionsSet.forEach((q, index) => {
      if (q.qv_cover_audio)
        formData.append(`qv_cover_audio_${index}`, q.qv_cover_audio);
      if (q.qv_cover_image)
        formData.append(`qv_cover_image_${index}`, q.qv_cover_image);
    });
    //
    onCreateClassAdmissionTestQuestions(axiosPrivate, formData)
      .then((response) => {
        const _data = response?.data;
        Swal.close();
        setIsSending(false);
        //
        if (_data?.status) {
          Swal.fire({
            title: "Admission Test Question's Creation",
            text: _data?.message,
            icon: "success",
            confirmButtonText: "OK",
            didOpen: () => {
              const btn = Swal.getConfirmButton(); //
              btn.style.backgroundColor = "#2840a8";
              btn.style.color = "white";
            },
          });
          setQuestionsSet([]);
        } else {
          Swal.fire({
            title: "Admission Test Question's Creation",
            text: _data?.message,
            icon: "info",
          });
        }
      })
      .catch((error) => {
        setIsSending(false);
        Swal.close();
        if (!error?.response) {
          Swal.fire({
            title: "Admission Test Question's Creation",
            text: "No server response",
            icon: "warning",
          });
        } else {
          Swal.fire({
            title: "Admission Test Question's Creation",
            text: error?.response?.data?.detail?.message,
            icon: "error",
          });
        }
      });
  };

  const onReleaseClassTestQuestion = async (
    class_test_question_id,
    is_activated,
  ) => {
    setIsSending(true);
    Swal.fire({
      title: `${is_activated ? "UnReleasing" : "Releasing"} question...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await wait(700);
    //
    onReleaseClassAdmissionTestQuestion(axiosPrivate, class_test_question_id, is_activated)
      .then((response) => {
        Swal.close();
        setIsSending(false);
        //
        if (response?.data?.status) {
          Swal.fire({
            title: "Class Test Questions Release",
            text: response?.data?.message,
            icon: "success",
          });
        }
      })
      .catch((error) => {
        if (!error?.response) {
          Swal.fire({
            title: "Class Test Questions DesActivation",
            text: "No server response",
            icon: "warning",
          });
        } else {
          Swal.fire({
            title: "Class Test Questions DesActivation",
            text: error?.response?.data?.detail?.message,
            icon: "error",
          });
        }
      });
  };

  const onArchiveClassTestQuestion = async (
    class_test_question_id,
    is_deleted,
  ) => {
    setIsSending(true);
    Swal.fire({
      title: "Creating questions...",
      title: `${is_deleted ? "Archiving" : "UnArchiving"} question...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    await wait(700);
    //
    onArchiveClassAdmissionTestQuestion(axiosPrivate, class_test_question_id, is_deleted)
      .then((response) => {
        Swal.close();
        setIsSending(false);
        if (response?.data?.status) {
          Swal.fire({
            title: "Class Test Questions Deletion",
            text: response?.data?.message,
            icon: "success",
          });
        }
      })
      .catch((error) => {
        if (!error?.response) {
          Swal.fire({
            title: "Class Test Questions Deletion",
            text: "No server response",
            icon: "warning",
          });
        } else {
          Swal.fire({
            title: "Class Test Questions Deletion",
            text: error?.response?.data?.detail?.message,
            icon: "error",
          });
        }
      });
  };

  return (
    <div className="admission-test">
      <div className="ad-test-inner">
        <div className="ad-test-inner-head">
          <div className="w-full d-flex justify-space-between">
            <Logo to={""} width={"120px"} />
            <Button
              type={"button"}
              preferedStyle={"default"}
              title={"⁉️ Add more questions"}
              action={() => setIsMoreQuestions(true)}
            />
          </div>
          <div className="w-full d-flex justify-space-between ad-test-inner-head-description">
            <div className="w-fit-content">
              <h1>Admission Test Questions Management</h1>
              <h2>Stream : {capitalizeAll(_stream_title)}</h2>
              <span>
                Stream created {moment(_stream_created_at).fromNow()} (
                {moment(_stream_created_at).format("LLLL")})
              </span>
              <div className="w-fit-content d-flex gap-md align-center pt-xs pb-xs">
                <StatusComponent
                  type={"default"}
                  title={`Total questions : ${admissionTestData?.total_activated_questions + admissionTestData?.total_inactivated_questions}`}
                />
                <StatusComponent
                  type={"success"}
                  title={`Total activated questions : ${admissionTestData?.total_activated_questions}`}
                />
                <StatusComponent
                  type={"error"}
                  title={`Total inactivated questions : ${admissionTestData?.total_inactivated_questions}`}
                />
              </div>
            </div>
            <div className="w-fit-content">
              <div className="pagination">
                <div className="p-wrapper">
                  <button
                    className={
                      admissionTestData?.current_page === 1
                        ? "button is-inactive"
                        : "button is-active"
                    }
                    onClick={() =>
                      setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1))
                    }
                    disabled={admissionTestData?.current_page === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {admissionTestData?.total_pages}{" "}
                    pages.
                  </span>
                  <button
                    className={
                      admissionTestData?.current_page ===
                      admissionTestData?.total_pages
                        ? "button is-inactive"
                        : "button is-active"
                    }
                    onClick={() =>
                      setCurrentPage((prev) =>
                        prev <= admissionTestData?.total_pages
                          ? prev + 1
                          : prev,
                      )
                    }
                    disabled={
                      admissionTestData?.current_page ===
                      admissionTestData?.total_pages
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ad-test-inner-body">
          {isEmpty(admissionTestData?.result) ? (
            <NotFound
              type={"data"}
              title={"No Admission Test Questions found!"}
              text={
                "No question designed yet for the admission test about this stream!"
              }
              isBoutton={false}
            />
          ) : (
            admissionTestData?.result?.map((item, idx) => (
              <div className="ad-test-inner-item" key={idx}>
                <ChallengeThemeQuestionItem
                  index={idx + 1}
                  mode={"admission-tests"}
                  question_data={{
                    question_id: item?.id,
                    qv_language: item?.language,
                    is_activated: item?.is_activated,
                    is_deleted: item?.is_deleted,
                    created_at: item?.created_at,
                    type: item?.type,
                    difficulty: item?.difficulty,
                    points: item?.points,
                    timing: item?.timing,
                    random_answer_order: item?.random_answer_order,
                    qv_cover_audio: item?.cover_audio,
                    qv_cover_image: item?.cover_image,
                    qv_description: item?.description,
                    qv_explanations: item?.explanations,
                    qv_answers: item?.answers,
                  }}
                  isActionsButton={true}
                  isDisplayLanguage={true}
                  onRelease={() =>
                    onReleaseClassTestQuestion(item?.id, item?.is_activated)
                  }
                  onArchive={() =>
                    onArchiveClassTestQuestion(item?.id, item?.is_deleted)
                  }
                  onUpdate={() => {}}
                />
              </div>
            ))
          )}
        </div>
      </div>
      {isMoreQuestions && (
        <div className="ad-test-outer fade-in">
          <div className="ad-test-outer-head">
            <h1>Admission Test Question's Composition</h1>
            {questionsSet.length > 0 && (
              <Button
                type={"button"}
                preferedStyle={"validate"}
                title={
                  isSending
                    ? "Creating Admission Test Question..."
                    : "✅ Create Admission Test Questions"
                }
                action={() => onSubmit()}
              />
            )}
            <Button
              type={"button"}
              preferedStyle={"default"}
              title={"Close"}
              action={() => setIsMoreQuestions(false)}
            />
          </div>
          <div className="ad-test-outer-body">
            <div className="ad-test-outer-item">
              <div className="w-full mb-md">
                <InputSelect
                  name={"type"}
                  data={[
                    {
                      key: "OCA",
                      title: "One Choice Answer",
                      is_activated: false,
                    },
                    {
                      key: "MCA",
                      title: "Multi Choices Answer",
                      is_activated: false,
                    },
                  ]}
                  placeholder={"Question Type(Answer's Type)"}
                  value={questionType}
                  onChange={(e) => setQuestionType(e?.target?.value)}
                />
              </div>
              <div className="w-full mb-md">
                <InputSelect
                  name={"difficulty"}
                  data={[
                    {
                      key: "beginner",
                      title: "Beginner",
                      is_activated: false,
                    },
                    {
                      key: "intermediate",
                      title: "Intermediate",
                      is_activated: false,
                    },
                    {
                      key: "advanced",
                      title: "Advanced",
                      is_activated: false,
                    },
                  ]}
                  placeholder={"Question Difficulty(Level)"}
                  value={questionDifficulty}
                  onChange={(e) => setQuestionDifficulty(e?.target?.value)}
                />
              </div>
              <div className="w-full mb-md">
                <InputSelect
                  name={"language"}
                  data={[
                    {
                      key: "en",
                      title: "English",
                      is_activated: false,
                    },
                    {
                      key: "fr",
                      title: "French",
                      is_activated: false,
                    },
                  ]}
                  placeholder={"Question Language"}
                  value={questionLanguage}
                  onChange={(e) => setQuestionLanguage(e?.target?.value)}
                />
              </div>
              <div className="w-full d-flex justify-space-between gap-md mb-md">
                <div className="w-50 timing-wrapper">
                  <span>Question Total Points</span>
                  <div className="w-full d-flex align-center gap-xs">
                    <input
                      className="w-60"
                      type="range"
                      name="points"
                      id="points"
                      min={10}
                      max={30}
                      step={1}
                      value={points}
                      onChange={(e) => setPoints(e.target.value)}
                    />
                    <output htmlFor="timing">{points} Points</output>
                  </div>
                </div>
                <div className="w-50 timing-wrapper">
                  <span>Question Timing</span>
                  <div className="w-full d-flex align-center gap-xs">
                    <input
                      className="w-60"
                      type="range"
                      name="timing"
                      id="timing"
                      min={10}
                      max={1800}
                      step={1}
                      value={timing}
                      onChange={(e) => setTiming(e.target.value)}
                    />
                    <output htmlFor="timing">{convertDuration(timing)}</output>
                  </div>
                </div>
              </div>
              <div className="w-full d-flex align-center gap-md mb-md">
                <span>Random Order of Answers ?</span>
                <CheckBox
                  id={"random-answer-order"}
                  checked={randomAnswerOrder}
                  onChange={(e) => setRandomAnswerOrder(e?.target?.checked)}
                />
              </div>
              {/* Audio process */}
              <div className="w-full mb-lg">
                <div className="browse-image">
                  <input
                    type="file"
                    id="audio_input"
                    accept="audio/mpeg,audio/wav,audio/mp3"
                    onChange={(e) => {
                      const _audioFile = onHandleSingleFile(e);
                      if (!_audioFile || !_audioFile.type.startsWith("audio/"))
                        return;

                      setAudioFile(_audioFile);
                      //   Create a temporary URL for the selected fil
                      const newPreviewUrl = URL.createObjectURL(_audioFile);
                      setAudioPreviewUrl(newPreviewUrl);
                    }}
                  />
                  <label htmlFor="audio_input">
                    <PiFileAudioBold className="icon" />
                    <span>Load Audio</span>
                  </label>
                </div>
                {audioPreviewUrl && (
                  <div className="files-container">
                    <div className="file-item file-item-audio">
                      <span className="file-icon">
                        <PiFileAudioBold className="icon" />
                      </span>
                      <div className="file-description">
                        <audio
                          className="w-full"
                          controls
                          controlsList="nodownload"
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          <source
                            src={audioPreviewUrl}
                            type={audioFile?.type}
                          />
                        </audio>
                      </div>
                      <span
                        className="file-action"
                        onClick={() => removeFile("audio")}
                      >
                        <SlClose className="icon" />
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {/* Image process */}
              <div className="w-full mb-lg">
                <div className="browse-image">
                  <input
                    type="file"
                    id="image_input"
                    accept="image/*"
                    onChange={(e) => {
                      const _imageFile = onHandleSingleFile(e);
                      if (!_imageFile || !_imageFile.type.startsWith("image/"))
                        return;

                      setImageFile(_imageFile);
                      //   Create a temporary URL for the selected fil
                      const newPreviewUrl = URL.createObjectURL(_imageFile);
                      setImagePreviewUrl(newPreviewUrl);
                    }}
                  />
                  <label htmlFor="image_input">
                    <SlPicture className="icon" />
                    <span>Load Illustrative Image</span>
                  </label>
                </div>
                {imagePreviewUrl && (
                  <div className="files-container">
                    <div className="file-item file-item-image">
                      <div className="file-description">
                        <img
                          src={imagePreviewUrl}
                          alt="cover illustration Preview"
                          onContextMenu={(e) => e.preventDefault()}
                        />
                      </div>
                      <span
                        className="file-action"
                        onClick={() => removeFile("image")}
                      >
                        <SlClose className="icon" />
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {/*  */}
              <div className="w-full content-wrapper">
                <span>Question Description</span>
                <hr />
                <CustomizedTextEditor
                  content={questionDescription}
                  setContent={setQuestionDescription}
                />
              </div>
              <div className="w-full content-wrapper">
                <span>Explanations Description</span>
                <hr />
                <CustomizedTextEditor
                  content={questionExplanation}
                  setContent={setQuestionExplanation}
                />
              </div>
              <div className="w-full content-wrapper">
                <span>Answers</span>
                <hr />
                <div className="answers-wrapper">
                  {isEmpty(questionAnswers) ? (
                    <p
                      style={{
                        color: "brown",
                        fontSize: "1rem",
                        fontStyle: "italic",
                      }}
                    >
                      Waiting for answers to be setup!
                    </p>
                  ) : (
                    questionAnswers?.map((row, idx) => (
                      <div className="answer-item" key={idx}>
                        {!isEmpty(questionType) && (
                          <div className="w-full d-flex justify-space-between align-center">
                            {questionType === "OCA" ? (
                              <RadioButton
                                id={`answer-option-${idx}`}
                                name={`answer-option-${idx}`}
                                title={"is the Right One ?"}
                                checked={row.is_correct}
                                onChange={() => onHandleRightAnswers(idx)}
                              />
                            ) : (
                              <CheckBox
                                id={`answer-option-${idx}`}
                                title={"is the Right One ?"}
                                checked={row.is_correct}
                                onChange={() => onHandleRightAnswers(idx)}
                              />
                            )}
                            <SlClose
                              className="icon"
                              onClick={() => onRemoveAnswer(idx)}
                            />
                          </div>
                        )}
                        <p
                          dangerouslySetInnerHTML={{
                            __html: row?.answer,
                          }}
                        />
                      </div>
                    ))
                  )}
                </div>
                <CustomizedTextEditor
                  content={questionAnswer}
                  setContent={setQuestionAnswer}
                />
                <div className="w-full d-flex justify-center align-center gap-md mt-md">
                  <Button
                    type={"button"}
                    preferedStyle={"default"}
                    title={"Add answer"}
                    action={() => onAddAnswer()}
                  />
                  <Button
                    type={"button"}
                    preferedStyle={"normal"}
                    title={"Validate Question"}
                    action={onAddQuestion}
                  />
                </div>
              </div>
            </div>
            <div className="ad-test-outer-item">
              {isEmpty(questionsSet) ? (
                <p
                  style={{
                    color: "brown",
                    fontSize: "0.875rem",
                    textAlign: "center",
                    margin: "1rem 0",
                  }}
                >
                  No question composed yet!
                </p>
              ) : (
                questionsSet.map((question, idx) => (
                  <ChallengeThemeQuestionItem
                    key={idx}
                    index={idx + 1}
                    question_data={question}
                    isActionsButton={false}
                    isDisplayLanguage={false}
                    isCheckBox={false}
                    isRemoveButton={true}
                    removeFunction={() => onRemoveQuestion(idx)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdmissionTest;
